import type { ColumnsType } from "antd/es/table";
import { Button, Typography } from "antd";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CustomTable } from "~/components";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import * as APITypes from "~/API";
import type {
  ListingAvailableUnitRow,
  ListingTenantContext,
} from "~/types/listing";
import { dateToLocalTime, getRouteWithParams } from "~/utils";
import { formatListingRent } from "~/utils/formatListingRent";

export function PlanAvailableUnitsTable({
  units,
  tenant,
}: {
  units: ListingAvailableUnitRow[];
  tenant: ListingTenantContext;
}) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const applyDisabled = tenant.status === APITypes.TenantStatusType.Active;

  const columns: ColumnsType<ListingAvailableUnitRow> = useMemo(
    () => [
      {
        title: t("Unit_Number"),
        dataIndex: "name",
        key: "name",
      },
      {
        title: t("Rent"),
        dataIndex: "rent",
        key: "rent",
        render: (rent: number) => formatListingRent(rent),
      },
      {
        title: t("Date_Of_Availability"),
        dataIndex: "availabilityDate",
        key: "availabilityDate",
        render: (availabilityDate: string | null) =>
          availabilityDate
            ? dateToLocalTime(availabilityDate, "dateTime")
            : "—",
      },
      {
        title: "",
        key: "actions",
        render: (_, row) => (
          <Button
            type="primary"
            size="small"
            disabled={applyDisabled}
            onClick={() =>
              navigate(
                getRouteWithParams("leaseApplication", {
                  entityType: "Unit",
                  entityID: row.id,
                })
              )
            }
          >
            {t("Apply_for_Lease")}
          </Button>
        ),
      },
    ],
    [applyDisabled, navigate, t]
  );

  return (
    <section style={{ marginTop: 24 }}>
      <CustomTable
        tableHeaders={
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t("Listing_details_available_units_title")}
          </Typography.Title>
        }
        mobileCardConfig={{
          titleKey: "name",
          subtitleKey: "rent",
          primaryKeys: ["availabilityDate"],
          hideDetails: true,
        }}
        tableProps={{
          rowKey: "id",
          pagination: false,
          dataSource: units,
          columns,
        }}
      />
    </section>
  );
}
